/**
 * Conditionally renders children based on the active user's permission.
 *
 * Three rendering modes:
 * - **allowed** → renders children normally
 * - **requires_approval** → renders children (approval modal is triggered
 *   at the action level when the user attempts the action). Set
 *   `allowApprovalFlow={false}` to hide instead.
 * - **denied** → renders `fallback` (or nothing)
 *
 * @example
 * ```tsx
 * <PermissionGate permission="dispenses:create">
 *   <Button onClick={handleCreate}>Create Dispense</Button>
 * </PermissionGate>
 * ```
 */

import * as React from "react";
import { usePermission } from "@/hooks/use-permission";
import type { Permission } from "@/lib/permissions";

interface PermissionGateProps {
  /** The permission key to check. */
  permission: Permission;
  /**
   * When true, children are rendered even if the permission requires
   * approval. The parent component is responsible for triggering
   * the AuthorizationApprovalModal before executing the action.
   */
  allowApprovalFlow?: boolean;
  /** Content to show when the user does not have permission. */
  fallback?: React.ReactNode;
  /** Content to show while the role is loading. */
  loading?: React.ReactNode;
  children: React.ReactNode;
}

export function PermissionGate({
  permission,
  allowApprovalFlow = true,
  fallback = null,
  loading = null,
  children,
}: PermissionGateProps) {
  const { isAllowed, needsApproval, isLoading } = usePermission(permission);

  if (isLoading) {
    return <>{loading}</>;
  }

  if (isAllowed) {
    return <>{children}</>;
  }

  if (needsApproval && allowApprovalFlow) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}
